/**
 * Pure matching helpers for the DataTable's per-column filter inputs.
 * Extracted so the matching rule is unit-testable without rendering.
 */

/** Lowercase + strip formatting chars ($, commas, %) so "$1,234" matches "1234". */
export function normalizeFilterText(s: string): string {
  return s.toLowerCase().replace(/[$,%]/g, '');
}

/**
 * Does `value` match a column filter `query`?
 *
 * Rule: the normalized value matches if the WHOLE value OR ANY whitespace-
 * separated token STARTS WITH the query.
 *
 * Why token-prefix (not whole-string startsWith, not substring includes):
 *  - Whole-string startsWith broke multi-field columns. The "Delivery" column's
 *    value is "City State Zip" (e.g. "Ottawa IL 61350"); typing a state ("IL")
 *    or zip ("613") never matched because they aren't a prefix of the whole
 *    string — only the leading city was searchable.
 *  - Plain substring `includes` would over-match short codes (typing "612" would
 *    hit the middle of SKU "STP612GR"). Token-prefix avoids that: a query only
 *    matches at a token boundary, so "612" still won't match inside "STP612GR".
 *
 * An empty/whitespace query matches everything; a null value matches nothing.
 */
export function columnFilterMatches(value: unknown, query: string): boolean {
  const q = normalizeFilterText(query.trim());
  if (!q) return true;
  if (value == null) return false;
  const norm = normalizeFilterText(String(value));
  if (norm.startsWith(q)) return true;
  return norm.split(/\s+/).some((tok) => tok.startsWith(q));
}
